import { useDispatch } from "react-redux";
import { setLoader } from "../../store/builder/reducer";
import { useEffect } from "react";
import { useSessionUserDetails } from "../../hooks/useUserDetails";
import { setUser } from "../../store/builder/loginSlice";
import routes, { isPublicPath } from "../../utils/routes";
import { useNavigate, useLocation } from "react-router-dom";
import { useAgencies } from "../../hooks/useAgencies";
import { useUpdateFcmToken } from "../../hooks/useNotifications";
import { getToken } from "firebase/messaging";
import { firebaseMessaging, refreshFCMToken } from "../../utils/firebase";
import {
  getUserToken,
  removeCurrentFbo,
  setFCMToken,
} from "../../utils/tokenHandler";

const AuthLayout = ({ children }) => {
  const hasToken = getUserToken();
  const {
    isLoading,
    data: userDetails,
    error,
    refetch: refetchUserDetails,
  } = useSessionUserDetails();
  const { data: agencyData } = useAgencies();
  const { mutate: updateFcmToken } = useUpdateFcmToken();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const updateTokens = async () => {
    try {
      let firebaseFcmToken = await getToken(firebaseMessaging, {
        vapidKey: import.meta.env.VITE_VAPID_KEY,
      });

      if (!firebaseFcmToken) {
        firebaseFcmToken = await refreshFCMToken();
      }

      if (firebaseFcmToken) {
        setFCMToken(firebaseFcmToken);
        updateFcmToken({ fcm_token: firebaseFcmToken });
      } else {
        console.warn("Failed to obtain valid FCM token after refresh");
      }
    } catch (tokenError) {
      console.error("Error updating FCM tokens:", tokenError);
    }
  };

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (error?.response?.status === 412) {
      navigate(routes.private.sso_warning);
    }
    if (error?.response?.status === 422) {
      removeCurrentFbo();
      refetchUserDetails();
    }
  }, [error, navigate, refetchUserDetails]);

  useEffect(() => {
    dispatch(setUser(userDetails));
  }, [userDetails, dispatch]);

  useEffect(() => {
    if (!hasToken && !isPublicPath(location.pathname)) {
      navigate(routes.public.login);
    }
  }, [hasToken, location.pathname, navigate]);

  useEffect(() => {
    const storedFbo = Number(localStorage.getItem('current-fbo'));
    const firstAgencyId = Number(agencyData?.[0]?.id);
    const hasValidStoredFbo = Number.isFinite(storedFbo) && storedFbo > 0;
    const hasValidAgencyId = Number.isFinite(firstAgencyId) && firstAgencyId > 0;

    if (!hasValidStoredFbo && hasValidAgencyId) {
      localStorage.setItem('current-fbo', String(firstAgencyId));
      if (hasToken) {
        refetchUserDetails();
        window.location.reload();
      }
    }
  }, [agencyData, hasToken, refetchUserDetails])

  useEffect(() => {
    if (userDetails) {
      updateTokens();
    }
  }, [userDetails]);

  // Refresh FCM token when app comes back into focus
  useEffect(() => {
    const handleAppFocus = () => {
      if (userDetails) {
        updateTokens();
      }
    };
    
    window.addEventListener('focus', handleAppFocus);
    return () => window.removeEventListener('focus', handleAppFocus);
  }, [userDetails]);

  return <>{children}</>;
};

export default AuthLayout;
